// src/modules/hr/pages/ExitManagement.jsx — Phase 23
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, Tag, Modal, Form, DatePicker, Input,
  InputNumber, message, Space, Descriptions, Popconfirm, Alert,
} from 'antd';
import { LogoutOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchEmployees, fetchExits, fetchExit, initiateExit, updateExitClearance, completeExit, cancelExit } from '../api';
import { EXIT_TYPE_OPTIONS, EXIT_STATUS_OPTIONS, CLEARANCE_STATUS_OPTIONS, tagColor, tagLabel } from '../constants/hrConstants';

const { Title, Text } = Typography;

export default function ExitManagement() {
  const { can } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => { fetchEmployees({ limit: 1000 }).then((r) => setEmployees(r?.data?.data || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await fetchExits(params);
      setData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await initiateExit({ ...v, resignationDate: v.resignationDate?.toISOString() });
      message.success('Exit initiated'); setAddOpen(false); form.resetFields(); load();
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const openDetail = async (row) => {
    try { const res = await fetchExit(row._id); setDetail(res?.data?.data || null); }
    catch (err) { message.error(err?.response?.data?.message || 'Failed'); }
  };

  const setClearance = async (key, status) => {
    try { const res = await updateExitClearance(detail._id, { key, status }); setDetail(res?.data?.data); load(); }
    catch (err) { message.error(err?.response?.data?.message || 'Failed'); }
  };

  const doComplete = async () => {
    try { await completeExit(detail._id, {}); message.success('Exit completed; account disabled'); setDetail(null); load(); }
    catch (err) { message.error(err?.response?.data?.message || 'Cannot complete'); }
  };

  const columns = [
    { title: 'Ref', dataIndex: 'reference', width: 110, render: (v) => <Text code>{v}</Text> },
    { title: 'Employee', dataIndex: ['employee', 'name'], render: (v, r) => r.employee?.name || '—' },
    { title: 'Type', dataIndex: 'exitType', width: 120, render: (v) => tagLabel(EXIT_TYPE_OPTIONS, v) },
    { title: 'Resignation', dataIndex: 'resignationDate', width: 120, render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
    { title: 'Last Working', dataIndex: 'lastWorkingDate', width: 120, render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
    { title: 'Status', dataIndex: 'status', width: 150, render: (v) => <Tag color={tagColor(EXIT_STATUS_OPTIONS, v)}>{tagLabel(EXIT_STATUS_OPTIONS, v)}</Tag> },
    { title: 'Actions', key: 'a', width: 160, fixed: 'right', render: (_, r) => <Button size="small" onClick={() => openDetail(r)}>Open</Button> },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><LogoutOutlined style={{ marginRight: 8 }} />Exit Management</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Resignation → clearance → account disable → final approval</Text>
        </Col>
        <Col>
          <Space>
            <Select allowClear placeholder="Status" style={{ width: 160 }} options={EXIT_STATUS_OPTIONS} value={statusFilter} onChange={(v) => setStatusFilter(v || null)} />
            <Button icon={<ReloadOutlined />} onClick={load} />
            {can('hrExit', 'create') && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Initiate Exit</Button>}
          </Space>
        </Col>
      </Row>

      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 1000 }} pagination={{ pageSize: 50 }} />

      <Modal title="Initiate Exit" open={addOpen} onCancel={() => setAddOpen(false)} onOk={submit} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={form} layout="vertical" initialValues={{ exitType: 'RESIGNATION', noticePeriodDays: 30, resignationDate: dayjs() }}>
          <Form.Item name="employeeId" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="exitType" label="Exit Type"><Select options={EXIT_TYPE_OPTIONS} /></Form.Item></Col>
            <Col span={12}><Form.Item name="noticePeriodDays" label="Notice Period (days)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={12}><Form.Item name="resignationDate" label="Resignation Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="reason" label="Reason"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title={detail ? `Exit — ${detail.employee?.name}` : ''} open={!!detail} onCancel={() => setDetail(null)} width={680}
        footer={[
          <Button key="c" onClick={() => setDetail(null)}>Close</Button>,
          detail && !['COMPLETED', 'CANCELLED'].includes(detail.status) && can('hrExit', 'edit') && <Popconfirm key="cancel" title="Cancel this exit and restore employee?" onConfirm={() => cancelExit(detail._id).then(() => { message.success('Cancelled'); setDetail(null); load(); })}><Button danger>Cancel Exit</Button></Popconfirm>,
          detail && detail.status === 'CLEARED' && can('hrExit', 'edit') && <Button key="done" type="primary" onClick={doComplete}>Complete & Disable Account</Button>,
        ].filter(Boolean)}
      >
        {detail && (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Reference">{detail.reference}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={tagColor(EXIT_STATUS_OPTIONS, detail.status)}>{tagLabel(EXIT_STATUS_OPTIONS, detail.status)}</Tag></Descriptions.Item>
              <Descriptions.Item label="Type">{tagLabel(EXIT_TYPE_OPTIONS, detail.exitType)}</Descriptions.Item>
              <Descriptions.Item label="Last Working">{detail.lastWorkingDate ? dayjs(detail.lastWorkingDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
            </Descriptions>

            {detail.pendingAssets?.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`${detail.pendingAssets.length} asset(s) still held`}
                description={detail.pendingAssets.map((a) => `${a.assetCode} ${a.name}`).join(', ')} />
            )}

            <Text strong>Clearance Checklist</Text>
            <Table style={{ marginTop: 8 }} size="small" rowKey="key" pagination={false} dataSource={detail.clearanceItems || []}
              columns={[
                { title: 'Item', dataIndex: 'label' },
                { title: 'Status', dataIndex: 'status', width: 160, render: (v, r) => (
                  <Select size="small" style={{ width: 130 }} value={v} options={CLEARANCE_STATUS_OPTIONS} disabled={['COMPLETED', 'CANCELLED'].includes(detail.status) || !can('hrExit', 'edit')} onChange={(nv) => setClearance(r.key, nv)} />
                ) },
              ]} />
          </>
        )}
      </Modal>
    </div>
  );
}
